import { useCallback } from "react";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { IMeetingLinkRecipientsViewModel } from "../models/meetingLinkRecipientsModel";
import { MeetingLinkRecipientsWarningMessage } from "../MeetingLinkRecipientsWarningMessage";

/**
 * After staff save the reservations of a remote (Google Meet) event the clients are emailed the
 * link. `warnClientsWithoutEmail` asks the API which of them hold a place but have no email and,
 * when there are any, tells the staff member in a dialog (with the link, to pass on by hand).
 *
 * Call it right after a successful save and don't await it: it never throws and a failed check
 * is silent (no error toast, no redirect), so it can never get in the way of the save itself.
 */
export const useMeetingLinkRecipientsWarning = () => {
  const { mutateAsync } = useApiMutation<IMeetingLinkRecipientsViewModel>();

  const warnClientsWithoutEmail = useCallback(
    async (eventId: number, userIds?: number[] | null) => {
      if (!Number.isInteger(eventId) || eventId <= 0) {
        return;
      }

      // An empty list would mean "every client of the event" to the API.
      const onlyUserIds = userIds?.filter((id) => Number.isInteger(id) && id > 0);

      try {
        const result = await mutateAsync({
          endpoint: endpointsMetadata.planner.meetingLinkRecipientsWithoutEmail,
          method: "post",
          payload: {
            eventId,
            userIds: onlyUserIds?.length ? onlyUserIds : null,
          },
          globalIsLoading: false,
          disableSuccessToast: true,
          disableErrorToast: true,
          disable401Redirect: true,
        });

        const clients = result?.clients ?? [];
        if (clients.length === 0) {
          return;
        }

        showCustomConfirmDialog(
          <MeetingLinkRecipientsWarningMessage
            clients={clients}
            meetingUrl={result?.meetingUrl}
          />,
          undefined,
          undefined,
          "warning",
          "Разбрах",
          null
        );
      } catch (error) {
        // The warning is a courtesy: the save already succeeded.
        console.error("Meeting link recipients check failed:", error);
      }
    },
    [mutateAsync]
  );

  return { warnClientsWithoutEmail };
};
